import { useCallback, useEffect, useState } from "react";
import { Button, ButtonGroup, Fade, FormControl, TextField } from "@mui/material";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import {
	changeSessionLifeTime,
	deleteRobot,
	editRobot,
	editRobotName,
	getRobot,
	selectActiveRobotId,
	selectActiveRobotVersion,
	selectDeletingRobot,
	selectEditingRobot,
	selectRobot,
} from "../../store/ses/sesRobotSlice";
import { getClusterServerModels, selectClusterServer } from "../../store/serverSlice";
import useAccessRight from "../../hooks/useAccessRight";
import useUncommittedRobotsList from "../../hooks/useUncommittedRobotsList";
import useTranslate from "../../hooks/useTranslate";
import { SEE, SES, SMC, SPR, TTS } from "../../constants/accessRights";
import { colorPrimary } from "../../constants/colors";
import { ExceptionType, RobotActionEventType, ServiceDataType } from "../../types/ses/sesRobotTypes";
import { RequestStatus, ResponseStatus } from "../../types/statusTypes";
import { Hint } from "@/HOC";
import EventsTab from "./EventsTab/EventsTab";
import ExceptionsTab from "./ExceptionsTab/ExceptionsTab";
import ServiceDataTab from "./ServiceDataTab/ServiceDataTab";
import ModelsTab from "./ModelsTab/ModelsTab";
import { AlertDialog, CustomTextField } from "../../ui";
import BlockWithLoading from "../../ui/BlockWithLoading/BlockWithLoading";
import BlockWithError from "../../ui/BlockWithError/BlockWithError";
import { IChangeFlg, NamesOfChangedKeys } from "../ConfigDataElem/ConfigDataElem.props";
import { IConfigRobotProps, TabsType } from "./ConfigRobot.props";
import styles from "./ConfigRobot.module.scss";
import { selectSesHints } from "@/store/hintsSlice";

const tabs: { id: TabsType; name: string }[] = [
	{ id: "events", name: "button_events" },
	{ id: "exceptions", name: "button_exceptions" },
	{ id: "serviceData", name: "button_data" },
	{ id: "models", name: "button_models" },
];

const ConfigRobot = ({ showSettings, setShowSettings }: IConfigRobotProps): JSX.Element => {
	const [inputRobotName, setInputRobotName] = useState<string>(""); // название робота
	const [inputDescription, setInputDescription] = useState<string>(""); // описание
	const [inputVoice, setInputVoice] = useState<string>(""); // голос
	const [inputMainRecognitionModel, setInputMainRecognitionModel] = useState<string>(""); // основная модель распознавания
	const [inputBackgroundRecognitionModel, setInputBackgroundRecognitionModel] = useState<string>(""); // фоновая модель распознавания
	const [inputSessionLifeTime, setInputSessionLifeTime] = useState<number>(0); // время жизни сессии, сек
	const [maxNumberExceptions, setMaxNumberExceptions] = useState<number>(0); // макс кол-во исключений подряд

	const [selectEvent, setSelectEvent] = useState<RobotActionEventType>("start"); // событие
	const [inputChannel, setInputChannel] = useState<string>("default"); // канал
	const [selectException, setSelectException] = useState<ExceptionType>("silence"); // исключение
	const [selectServiceData, setSelectServiceData] = useState<ServiceDataType>("yes"); // сервисные данные

	const [visibleBlockConfig, setVisibleBlockConfig] = useState<TabsType>("events"); // табы
	const [changeFlg, setChangeFlg] = useState<IChangeFlg>({ thisIs: false, listOfChanges: [] }); // флаг, уведомляющий об изменении данных и возможности сохранить эти изменения
	const [showAlertDialogDel, setShowAlertDialogDel] = useState<boolean>(false); // показ диалогового окна при удалении робота
	const [showAlertDialogDelConfirm, setShowAlertDialogDelConfirm] = useState<boolean>(false); // показ диалогового окна подтверждения удаления робота

	const dispatch = useAppDispatch();
	const robotInfo = useAppSelector(selectRobot); // store - информация о роботе
	const editingRobot = useAppSelector(selectEditingRobot); // store - статус изменения данных робота
	const deletingRobot = useAppSelector(selectDeletingRobot); // store - статус удаления робота
	const { smc: clusterServerSmc, see: clusterServerSee, spr: clusterServerSpr, tts: clusterServerTts } = useAppSelector(selectClusterServer); // store - список моделей на сервере cluster
	const activeRobotId = useAppSelector(selectActiveRobotId); // store - id активного робота
	const activeRobotVersion = useAppSelector(selectActiveRobotVersion); // store - версия активного робота
	const sesHints = useAppSelector(selectSesHints);

	const uncommittedRobotsList = useUncommittedRobotsList(); // hook для обработки списка незакоммиченных настроек роботов
	const isAccess = useAccessRight(); // hook для проверки прав доступа
	const translate = useTranslate(); // hook для перевода текста

	// обработчик установки флага изменений
	const changeFlgHandler = useCallback(
		(changed: NamesOfChangedKeys): void => {
			if (!changeFlg.thisIs || !changeFlg.listOfChanges.includes(changed)) {
				setChangeFlg((prev) => ({ thisIs: true, listOfChanges: [...prev.listOfChanges, changed] })); // ставим флаг о несохраненных данных
			}
		},
		[changeFlg]
	);

	// следим за открытием вкладки
	useEffect(() => {
		// при открытии
		if (showSettings) {
			isAccess(SMC.LIST) && clusterServerSmc.modelList.length === 0 && dispatch(getClusterServerModels({ serviceType: "smc" })); // получаем список моделей smc
			isAccess(SEE.LIST) && clusterServerSee.modelList.length === 0 && dispatch(getClusterServerModels({ serviceType: "see" })); // получаем список моделей see
			isAccess(SPR.LIST) && clusterServerSpr.modelList.length === 0 && dispatch(getClusterServerModels({ serviceType: "spr" })); // получаем список моделей spr
			isAccess(TTS.LIST) && clusterServerTts.modelList.length === 0 && dispatch(getClusterServerModels({ serviceType: "tts" })); // получаем список моделей tts
		}
	}, [showSettings]);

	// следим за id робота
	useEffect(() => {
		// сбрасываем select'ы
		setSelectEvent("start");
		setInputChannel("default");
		setSelectException("silence");
		setSelectServiceData("yes");
		setVisibleBlockConfig("events"); // сбрасываем tab
		// сбрасывааем поля
		setInputRobotName("");
		setInputDescription("");
		setInputVoice("");
		setInputMainRecognitionModel("");
		setInputBackgroundRecognitionModel("");
		setInputSessionLifeTime(0);
		setChangeFlg({ thisIs: false, listOfChanges: [] }); // сбрасываем флаг о несохраненных данных
	}, [robotInfo.data?.id]);

	// следим за получением данных робота
	useEffect(() => {
		// если есть данные - вписываем в поля
		if (robotInfo.data) {
			!inputRobotName && setInputRobotName(robotInfo.data.name);
			!inputDescription && setInputDescription(robotInfo.data.description);
			!inputVoice && setInputVoice(robotInfo.data.voice);
			!inputMainRecognitionModel && setInputMainRecognitionModel(robotInfo.data.recognition?.main);
			!inputBackgroundRecognitionModel && setInputBackgroundRecognitionModel(robotInfo.data.recognition?.background);
			!inputSessionLifeTime && setInputSessionLifeTime(robotInfo.data.session.lifetime);
		}
	}, [robotInfo.data]);

	// следим за статусом изменения робота
	useEffect(() => {
		// если изменение данных робота прошло успешно
		if (editingRobot.status === RequestStatus.IDLE && editingRobot.error === ResponseStatus.SUCCESS && editingRobot.message !== "") {
			robotInfo.data && robotInfo.data.name !== inputRobotName && dispatch(editRobotName({ robotId: robotInfo.data.id, robotName: inputRobotName })); // если изменилось имя - меняем в store
			setChangeFlg({ thisIs: false, listOfChanges: [] }); // сбрасываем флаг о несохраненных данных
			uncommittedRobotsList("add"); // пишем в куки id незакомиченного робота
		}
	}, [editingRobot]);

	// следим за статусом удаления робота
	useEffect(() => {
		// если прошло успешно
		if (deletingRobot.status === RequestStatus.IDLE && deletingRobot.error === ResponseStatus.SUCCESS && deletingRobot.message !== "") {
			setShowSettings(false); // закрываем вкладку
		}
	}, [deletingRobot]);

	// следим за временем жизни сессии
	useEffect(() => {
		// через пол-секунды бездействия после окончания ввода
		const handler = setTimeout(() => {
			if (robotInfo.data === null) return;
			if (robotInfo.data.session.lifetime !== inputSessionLifeTime) {
				dispatch(changeSessionLifeTime(inputSessionLifeTime)); // изменение времени жизни сессии
				changeFlgHandler("session"); // ставим флаг о несохраненных данных
			}
		}, 500);

		return () => {
			clearTimeout(handler); // сбрасываем timeout, если продолжается ввод
		};
	}, [inputSessionLifeTime]);

	// обработчик сохранения данных робота
	const editRobotHandler = (): void => {
		// изменяем данные робота
		robotInfo.data &&
			dispatch(
				editRobot({
					robotId: robotInfo.data.id,
					data: {
						name: changeFlg.listOfChanges.includes("name") ? inputRobotName : undefined,
						description: changeFlg.listOfChanges.includes("description") ? inputDescription : undefined,
						voice: changeFlg.listOfChanges.includes("voice") ? inputVoice : undefined,
						actions: changeFlg.listOfChanges.includes("actions") ? robotInfo.data.actions : undefined,
						exceptions: changeFlg.listOfChanges.includes("exceptions") ? robotInfo.data.exceptions : undefined,
						servicedata: changeFlg.listOfChanges.includes("servicedata") ? robotInfo.data.servicedata : undefined,
						models: changeFlg.listOfChanges.includes("models") ? robotInfo.data.models : undefined,
						session: changeFlg.listOfChanges.includes("session") ? robotInfo.data.session : undefined,
						recognition: changeFlg.listOfChanges.includes("recognition") ? { main: inputMainRecognitionModel, background: inputBackgroundRecognitionModel } : undefined,
					},
				})
			); // изменяем данные робота
	};

	// обработчик удаления робота
	const deleteRobotHandler = (): void => {
		robotInfo.data && dispatch(deleteRobot(robotInfo.data.id)); // удаление робота
	};

	return (
		<div className={styles.configRobot}>
			{/* загрузка настроек робота */}
			{robotInfo.status === RequestStatus.LOADING && <BlockWithLoading />}

			{/* ошибка получения настроек робота */}
			{robotInfo.status === RequestStatus.FAILED && (
				<BlockWithError
					message={robotInfo.message}
					updateHandler={() => {
						isAccess(SES.ROBOT_GET) && activeRobotId && dispatch(getRobot(activeRobotId)); // получаем инфо о роботе;
					}}
				/>
			)}

			{/* настройки робота */}
			{robotInfo.status === RequestStatus.IDLE && robotInfo.data && (
				<Fade in={true} timeout={500}>
					<div className={styles.configRobotWrapper}>
						<div className={styles.configRobotTop}>
							<div className={styles.configRobotTopMain}>
								{/* название робота */}
								<div className={styles.configRobotTopMainContainer}>
									<FormControl fullWidth sx={{ mt: "6px" }}>
										<TextField
											required
											label={translate("input_name")}
											variant="outlined"
											disabled={!isAccess(SES.ROBOT_EDIT) || activeRobotVersion !== "draft"}
											value={inputRobotName}
											onChange={(e) => {
												setInputRobotName(e.target.value);
												changeFlgHandler("name"); // ставим флаг о несохраненных данных
											}}
											error={!inputRobotName}
											InputProps={{
												style: {
													height: 33,
													fontSize: 13,
													color: colorPrimary,
												},
											}}
											InputLabelProps={{
												style: {
													fontSize: 13,
												},
											}}
											sx={{ '.MuiInputLabel-root[data-shrink="false"]': { top: -8 } }}
										/>
									</FormControl>
									{/* время жизни сессии */}
									<FormControl fullWidth sx={{ mt: "6px" }}>
										<Hint hint={sesHints.hint_sesFieldSessionLifetime} position="bottom" offset={[0, -8]}>
											<TextField
												required
												label={translate("input_sessionLifetime")}
												variant="outlined"
												type="Number"
												fullWidth
												value={inputSessionLifeTime}
												onChange={(e) => setInputSessionLifeTime(Number(e.target.value))}
												disabled={!isAccess(SES.ROBOT_EDIT) || activeRobotVersion !== "draft"}
												error={inputSessionLifeTime < 0}
												InputProps={{
													style: {
														height: 33,
														fontSize: 13,
														color: colorPrimary,
													},
													inputProps: { step: 1, min: 0 },
												}}
												InputLabelProps={{
													style: {
														fontSize: 13,
													},
												}}
												sx={{ '.MuiInputLabel-root[data-shrink="false"]': { top: -8 } }}
											/>
										</Hint>
									</FormControl>
								</div>

								{/* описание */}
								<CustomTextField
									label={translate("input_description")}
									variant="outlined"
									multiline
									minRows={2}
									maxRows={2}
									disabled={!isAccess(SES.ROBOT_EDIT) || activeRobotVersion !== "draft"}
									value={inputDescription}
									onChange={(e) => {
										setInputDescription(e.target.value);
										changeFlgHandler("description"); // ставим флаг о несохраненных данных
									}}
									InputLabelProps={{
										style: {
											fontSize: 13,
										},
									}}
									sx={{ width: "100%" }}
								/>

								{/* табы */}
								<ButtonGroup fullWidth>
									{tabs.map(({ id, name }) => (
										<Button
											key={id}
											variant={visibleBlockConfig === id ? "contained" : "outlined"}
											sx={{ overflow: "hidden", fontSize: 11 }}
											onClick={() => setVisibleBlockConfig(id)}
										>
											<Hint hint={sesHints[`${name.replace("button_", "hint_ses")}Tag`]} position="top" offset={[0, -2]}>
												<>{translate(name)}</>
											</Hint>
										</Button>
									))}
								</ButtonGroup>
							</div>

							{visibleBlockConfig === "events" && (
								<EventsTab
									robotData={robotInfo.data}
									changeFlg={changeFlg}
									changeFlgHandler={changeFlgHandler}
									selectEvent={selectEvent}
									setSelectEvent={setSelectEvent}
									inputChannel={inputChannel}
									setInputChannel={setInputChannel}
									editRobotHandler={editRobotHandler}
								/>
							)}

							{visibleBlockConfig === "exceptions" && (
								<ExceptionsTab
									robotData={robotInfo.data}
									changeFlg={changeFlg}
									changeFlgHandler={changeFlgHandler}
									selectException={selectException}
									setSelectException={setSelectException}
									maxNumberExceptions={maxNumberExceptions}
									setMaxNumberExceptions={setMaxNumberExceptions}
									inputChannel={inputChannel}
									setInputChannel={setInputChannel}
									editRobotHandler={editRobotHandler}
								/>
							)}

							{visibleBlockConfig === "serviceData" && (
								<ServiceDataTab
									robotData={robotInfo.data}
									changeFlgHandler={changeFlgHandler}
									selectServiceData={selectServiceData}
									setSelectServiceData={setSelectServiceData}
								/>
							)}

							{visibleBlockConfig === "models" && (
								<ModelsTab
									robotData={robotInfo.data}
									changeFlgHandler={changeFlgHandler}
									inputVoice={inputVoice}
									setInputVoice={setInputVoice}
									inputMainRecognitionModel={inputMainRecognitionModel}
									setInputMainRecognitionModel={setInputMainRecognitionModel}
									inputBackgroundRecognitionModel={inputBackgroundRecognitionModel}
									setInputBackgroundRecognitionModel={setInputBackgroundRecognitionModel}
								/>
							)}
						</div>

						{activeRobotVersion !== "backup" && (
							<div className={styles.configRobotBottomBtns}>
								{/* сохранить */}
								{isAccess(SES.ROBOT_EDIT) && (
									<FormControl fullWidth>
										<Hint hint={"hint_buttonSave"} width="100%">
											<Button variant="outlined" disabled={!changeFlg.thisIs} sx={{ fontSize: 11 }} fullWidth onClick={editRobotHandler}>
												{translate("button_save")}
											</Button>
										</Hint>
									</FormControl>
								)}
								{/* удалить */}
								{isAccess(SES.ROBOT_DELETE) && activeRobotVersion !== "prod" && (
									<FormControl fullWidth>
										<Button variant="outlined" sx={{ fontSize: 11 }} onClick={() => setShowAlertDialogDel(true)} color="error">
											{translate("button_deleteRobot")}
										</Button>
									</FormControl>
								)}
							</div>
						)}
					</div>
				</Fade>
			)}

			{/* удаление робота */}
			<AlertDialog
				showAlertDialog={showAlertDialogDel}
				setShowAlertDialog={setShowAlertDialogDel}
				submitHandler={() =>
					setTimeout(() => {
						setShowAlertDialogDelConfirm(true);
					}, 300)
				}
				title="dialog_deleteRobot"
				description="dialog_deleteRobotConfirm"
				name={robotInfo.data?.name}
			/>
			{/* подтверждение удаления робота */}
			<AlertDialog
				showAlertDialog={showAlertDialogDelConfirm}
				setShowAlertDialog={setShowAlertDialogDelConfirm}
				submitHandler={deleteRobotHandler}
				title="dialog_deleteRobotRepeat"
				description="dialog_deleteRobotRepeatConfirm"
			/>
		</div>
	);
};

export default ConfigRobot;
